import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SearchX, Search as SearchIcon } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/common/Reveal";
import ProductCard from "@/components/product/ProductCard";
import EmptyState from "@/components/common/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { productsService } from "@/services/productsService";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [input, setInput] = useState(q);
  const [results, setResults] = useState(null);

  useEffect(() => { setInput(q); }, [q]);

  useEffect(() => {
    setResults(null);
    if (!q.trim()) { setResults([]); return; }
    productsService.search(q).then(setResults);
  }, [q]);

  const submit = (e) => { e.preventDefault(); setSearchParams(input.trim() ? { q: input.trim() } : {}); };

  return (
    <>
      <Seo title={q ? `Search: ${q}` : "Search"} description="Search Athena Scientific laboratory products." />
      <div className="border-b border-border bg-[#f9fafb] grid-tech">
        <div className="container-x py-12 md:py-16">
          <Reveal>
            <p className="eyebrow">Search</p>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight text-ink">Find your product</h1>
            <form onSubmit={submit} className="mt-6 relative max-w-2xl">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-greyish" />
              <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="search-page-input" autoFocus placeholder="Search products, brands, categories…" className="w-full pl-12 pr-28 py-4 border border-border rounded-md text-lg outline-none focus:border-aqua focus:ring-2 focus:ring-aqua/20 transition-all bg-white" />
              <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 bg-aqua text-white px-5 py-2.5 rounded-md font-semibold hover:bg-aqua-dark transition-all">Search</button>
            </form>
          </Reveal>
        </div>
      </div>

      <div className="container-x py-12 md:py-16">
        {!q.trim() ? (
          <p className="text-greyish" data-testid="search-prompt">Enter a search term above to find products.</p>
        ) : results === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-lg" />)}</div>
        ) : results.length === 0 ? (
          <EmptyState icon={SearchX} title={`No results for “${q}”`} description="Try different keywords, or browse the full catalogue." actionLabel="Browse Products" actionTo="/products" testId="search-no-results" />
        ) : (
          <>
            <p className="text-sm text-greyish mb-6" data-testid="search-results-count">{results.length} result{results.length !== 1 ? "s" : ""} for “{q}”</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{results.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
          </>
        )}
      </div>
    </>
  );
}
